// The desktop mini timer bar — /popup/timer-bar.
// Desktop 1.0.13 timer bar (James, 2026-10-01): "If the user has 2 monitors, I
// don't want the time entry widget to only stay within the app window."
// One-click timers (docs/specs/timers-one-click-spec.md, Track B, 2026-10-14).
//
// The desktop app loads this page into a small frameless window (~380x48) that
// can be dragged onto any monitor and floats above Word / Outlook.  It is a
// window of its OWN, so everything it knows comes from the shared localStorage
// of the 'persist:lextrack' partition:
//   • the timer slots — what is running, for how long (lib/timerStore.js
//     listMyTimers, through hooks/useTimers.js, which also runs the
//     one-running reconciler here like in every other new-bundle window);
//   • the ownership heartbeats — which timers still have an open window;
//   • the sign-in token — whether anyone is signed in, and who.
//
// With one-click timers the SLOT is the clock, so what a button does depends
// on who wrote the slot:
//   • a v2 timer (written by the new code): ⏯ is a direct store transition
//     — ⏸ timerStore.pause, ▶ timerStore.startOrResume (the one-running
//     rule: resuming one pauses my others at the same instant), chosen by
//     what the bar showed, once per click (TOGGLE_GUARD_MS) — and ↗ is
//     lib/timerWindows openTimerForm (focus the window already on it, else
//     open one on its slot).  A ⏸ that paused the timer then does what ↗
//     does, so its form pops up (James, 2026-10-15); ▶ opens nothing;
//   • a timer from before the update (v1): exactly today's path — ⏯ posts a
//     command the owning window applies (lib/timerCommands.js) and waits for
//     its acknowledgement (a 'pause' also brings that window forward); a
//     timer no window holds is reopened on its saved slot; the main window's
//     legacy timer is sent to the main window.
//   • "⏱ Start a timer" and "+" start a timer HEADLESS — no window opens, the
//     bar simply shows it — when the person may record time (can_post_time);
//     otherwise they open a timer form window as before (its save would 403).
// It writes 'lextrack_timer_bar_proto' = 2 so the main window's Timers panel
// knows this bar runs the new code (an old bar, until the app restarts, makes
// the panel say "Restart Omnia to finish updating the timer bar").
//
// "Is that timer's window still open?" is the question every window click
// turns on, and getting it wrong in one direction opens a SECOND window on the
// same timer (both can then be saved — the time billed twice).  So:
//   • a window that closes REMOVES its ownership record; no record = closed;
//   • a record that has merely gone quiet is NOT read as closed — Chromium
//     throttles a minimized window to one heartbeat a minute — the bar sends
//     the command and waits for the window to acknowledge that exact click;
//   • whenever the bar does (re)open a window it uses the popupKey the window
//     recorded, so if that window does still exist the desktop app focuses it
//     instead of opening another;
//   • a window that still holds the timer but recorded NO popupKey is never
//     reopened under a guessed one, even when it does not answer: the guess
//     would not match its real key, so the desktop app would open a second
//     window on the timer (seam audit, Desktop 1.0.13 timer bar (James,
//     2026-10-01) — a hung window then came back paused beside a duplicate
//     still ticking).  It is asked to come forward instead.
//
// The bar is 48px tall: it cannot show the app's confirm dialog.  A start
// that needs one (a timer from before the update may still be ticking in
// another window) starts nothing and says so on the bar instead.
//
// Rendered OUTSIDE PopupLayout on purpose: no report-issue chip, no title
// strip, no ProtectedRoute (a redirect to the sign-in form would render a
// login page in a 48px window).  Signed out, it shows nothing but a link back
// to the main window — no matter names, no times.

import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { getToken, safeOpenPopupWindow } from '../services/token'
import { hasPermission } from '../lib/navItems'
import { hasTimerBar } from '../lib/desktopCapabilities'
import { sendTimerCommand } from '../lib/timerCommands'
import { timerWindowOptions } from '../utils/desktopVersion'
import useWindowDrag from '../hooks/useWindowDrag'
import useTimers from '../hooks/useTimers'
import timerStore, { MESSAGES } from '../lib/timerStore'
import { COMMAND_ACK_MS, awaitAck, awaitHeldPauses, openFormAfterHeldPause, openTimerForm, reopenTimerWindow } from '../lib/timerWindows'
import {
  BAR_PROTO_VERSION, LEGACY_SLOT_KEY, TIMER_BAR_PROTO_KEY, decodeTokenUserId, formatBarElapsed,
  isLegacySlotKey, ownerState, popupKeyForSlot, readOwner, tagRouteWithPopupKey
} from '../lib/timerSlots'

// How long the bar waits for a timer window to ACKNOWLEDGE a click before
// acting on the silence (frozen, mid-reload, crashed) — now shared with every
// other sender in lib/timerWindows.js; re-exported for the bar's tests.
export { COMMAND_ACK_MS }

// One click = one timer: a double click on Start or "+" would otherwise start
// two, the second pausing the first at 0:00:00 (a leftover "No matter yet"
// timer).  Same window as every other one-click start.
const START_GUARD_MS = 1000
// One click on ⏯ = one change.  The slot changes synchronously and the bar
// re-renders before the next click arrives, so the second click of a double
// click would otherwise undo the first (⏸ then ▶ — the timer keeps running;
// incident 096: a double click once doubled an hour).  So ⏯ ignores the
// second click of a double click (MouseEvent.detail ≥ 2 — keyboard activation
// is detail 0 and always counts), for every timer, and a second ⏯ on the same
// v2 timer within this window (a touch double tap may report detail 1 twice).
// A timer from before the update keeps today's path: its window's own gate
// applies a duplicate command once.
export const TOGGLE_GUARD_MS = 500
// A short message on the bar (in place of the matter) stays this long; a
// storage failure stays until a later action works.
const NOTICE_MS = 8000

const COLORS = {
  bg: '#1f2937',
  border: '#374151',
  text: '#f9fafb',
  muted: '#9ca3af',
  ticking: '#4ade80',
  paused: '#fbbf24',
  button: '#374151',
  buttonHover: '#4b5563',
  accent: '#7c3aed'
}

// -webkit-app-region: the bar's background, clock and matter name drag the
// window natively.  Buttons must opt out (on Windows a native drag region
// swallows clicks), so on desktop 1.0.14+ a MOUSE press-and-hold that MOVES
// from a button drags the bar too — useWindowDrag / lib/windowDrag.js — while
// a press that does not move is still a click.  A touch or pen press on a
// button stays a plain tap (see lib/windowDrag.js for why).
const DRAG = { WebkitAppRegion: 'drag' }
const NO_DRAG = { WebkitAppRegion: 'no-drag' }

// Sizes follow the window's height.  Chromium keeps one zoom level per ORIGIN,
// so zooming the main Omnia window (Ctrl+=) zooms this page too, inside a
// window whose pixel size is fixed — fixed px sizes would then overflow the
// 48px bar.  At 100% these resolve to the plain values (30px buttons, 15/13px
// text); zoomed in, they shrink with the viewport instead of being clipped.
const BTN = 'min(30px, calc(100vh - 12px))'
const TIME_FONT = 'min(15px, 40vh)'
const TEXT_FONT = 'min(13px, 34vh)'
const SMALL_FONT = 'min(12px, 32vh)'

const iconButton = {
  ...NO_DRAG,
  flex: 'none',
  width: BTN,
  height: BTN,
  borderRadius: 8,
  border: 'none',
  background: COLORS.button,
  color: COLORS.text,
  fontSize: TEXT_FONT,
  lineHeight: 1,
  padding: 0,
  cursor: 'pointer'
}

// The desktop app hands +N menu labels straight to the OS and bounds them
// (desktop/timerBar.js); keep well inside that.
const MENU_MAX_ITEMS = 30
const MENU_MAX_LABEL = 100
const clip = (text, n) => (text.length > n ? `${text.slice(0, n - 1)}…` : text)

const newPopupKey = () => `timer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

// The bar cannot ask (see the header): what it says instead when a start or
// resume would need the "Start anyway" confirm.
export const needsConfirmMessage = (label) =>
  `"${label || MESSAGES.noMatterLabel}" may still be running in another Omnia window — pause it there, then try again`

function readStorage () {
  try { return window.localStorage } catch { return null }
}

// Which timer the bar shows.  `rows` come from listMyTimers, already sorted
// ticking first (EFFECTIVELY ticking — a timer a later start has cut reads as
// paused), then by last activity, then by key; so the automatic choice is the
// first row.  A timer the person PICKED from the +N menu (`selected` =
// { key, at }) stays shown until another timer is started or resumed after
// the pick, so starting a timer always brings it onto the bar.
export function chooseCurrentRow (rows, selected) {
  const list = Array.isArray(rows) ? rows.filter(r => r && r.key) : []
  if (list.length === 0) return null
  if (selected && selected.key) {
    const picked = list.find(r => r.key === selected.key)
    if (picked) {
      const pickedAt = Number(selected.at) || 0
      const newer = list.some(r => r.key !== picked.key && r.ticking && r.lastActivity > pickedAt)
      if (!newer) return picked
    }
  }
  return list[0]
}

export default function TimerBar () {
  const { user, refreshUser, loading } = useAuth() || {}
  const desktop = hasTimerBar()
  const bar = desktop ? window.electronAPI.timerBar : null

  // My timers, live.  forceTick: the bar re-reads once a second even when
  // nothing ticks (as it always has) — owner records age from live to quiet
  // to dead with no event, and a token cleared by THIS window fires none.
  // A render is skipped when nothing visible changed.
  const { timers } = useTimers({ forceTick: true })
  // A timer the person picked from the +N menu: { key, at }.  See
  // chooseCurrentRow — a newer start/resume takes the bar back.
  const [selection, setSelection] = useState(null)
  // { text, persistent } shown in place of the matter name (see NOTICE_MS).
  const [notice, setNotice] = useState(null)
  const noticeTimer = useRef(null)
  // slot key → the timeout waiting for that slot's latest acknowledgement.
  // One per slot: a newer click supersedes an older one, so a quick
  // pause-then-resume never reads the first click's reply as missing.
  const pendingAcks = useRef(new Map())
  // Cancels for awaitHeldPauses waits (old-bundle windows sent a 'pause').
  const heldWaits = useRef(new Set())
  const lastStartAt = useRef(-Infinity)
  // slot key → when this bar last applied ⏯ to it (TOGGLE_GUARD_MS).
  const lastToggleAt = useRef(new Map())
  const refreshedFor = useRef(null)
  // Press-and-hold anywhere on the bar — buttons included — moves it (desktop
  // 1.0.14+, mouse only; on 1.0.13 `bar` has no dragStart and these props are
  // empty).
  // Must run before the early returns below: hooks cannot be skipped.
  const dragProps = useWindowDrag({
    api: bar,
    canStart: (e) => (e.target && typeof e.target.closest === 'function' ? e.target.closest('button') : null)
  })

  // The window is transparent so the bar can have rounded corners; the app's
  // themed body background would otherwise paint a square behind it.
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prev = {
      htmlBg: html.style.background, bodyBg: body.style.background,
      bodyMargin: body.style.margin, bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow, title: document.title
    }
    html.style.background = 'transparent'
    body.style.background = 'transparent'
    body.style.margin = '0'
    body.style.overflow = 'hidden'
    html.style.overflow = 'hidden'
    document.title = 'Omnia Timer'
    return () => {
      html.style.background = prev.htmlBg
      body.style.background = prev.bodyBg
      body.style.margin = prev.bodyMargin
      body.style.overflow = prev.bodyOverflow
      html.style.overflow = prev.htmlOverflow
      document.title = prev.title
    }
  }, [])

  // The protocol marker: this bar runs the new code.  Written again if a
  // "Reset UI preferences" (lib/uiPrefs.js) or another bulk clear removes it,
  // so the panel never asks for a restart this bar does not need.
  useEffect(() => {
    if (!hasTimerBar()) return undefined
    const mark = () => {
      try {
        const want = String(BAR_PROTO_VERSION)
        if (window.localStorage.getItem(TIMER_BAR_PROTO_KEY) !== want) window.localStorage.setItem(TIMER_BAR_PROTO_KEY, want)
      } catch { /* storage unavailable — the panel then just suggests a restart */ }
    }
    mark()
    const onStorage = (e) => {
      if (!e || e.key === null || e.key === TIMER_BAR_PROTO_KEY) mark()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  useEffect(() => {
    const pending = pendingAcks.current
    const waits = heldWaits.current
    return () => {
      pending.forEach(clearTimeout)
      pending.clear()
      waits.forEach(cancel => { try { cancel() } catch {} })
      waits.clear()
      if (noticeTimer.current) clearTimeout(noticeTimer.current)
    }
  }, [])

  const token = getToken()
  const uid = decodeTokenUserId(token)
  // The loaded user is the token's user (a stale user from before a sign-in
  // change never decides anything here).
  const userIsSignedIn = !!(user && uid && String(user.id) === uid)
  const canPost = userIsSignedIn && hasPermission(user, 'can_post_time')

  // The bar page stays loaded while it is hidden (signed out), so a sign-in
  // made in the main window afterwards reaches it as a token with no user —
  // AuthProvider reads /me only on mount.  Re-read it once per signed-in id,
  // or "Start" would never be one-click until the app restarted.
  useEffect(() => {
    if (!uid || loading || (user && String(user.id) === uid)) return
    if (refreshedFor.current === uid || typeof refreshUser !== 'function') return
    refreshedFor.current = uid
    try { Promise.resolve(refreshUser()).catch(() => {}) } catch { /* keep the bar up */ }
  }, [uid, user, loading, refreshUser])

  const current = chooseCurrentRow(timers, selection)

  const showNotice = useCallback((text, { persistent = false } = {}) => {
    if (noticeTimer.current) { clearTimeout(noticeTimer.current); noticeTimer.current = null }
    if (!text) { setNotice(null); return }
    setNotice({ text, persistent })
    if (!persistent) {
      noticeTimer.current = setTimeout(() => {
        noticeTimer.current = null
        setNotice(n => (n && !n.persistent ? null : n))
      }, NOTICE_MS)
    }
  }, [])

  // What a store result means on the bar.  A storage failure is persistent
  // (time is not being recorded); a Cancel is silent; old-bundle windows that
  // were sent a 'pause' are watched for their ack ("Pause "<label>" in its
  // window" on silence, never treated as success).
  const report = useCallback((r) => {
    if (!r) return
    if (!r.ok) {
      if (r.reason === 'storage') { showNotice(MESSAGES.storage, { persistent: true }); return }
      if (r.reason === 'cancelled') return
      if (r.reason === 'needs-confirm') { showNotice(needsConfirmMessage(r.label)); return }
      showNotice(r.message || MESSAGES.invalid)
      return
    }
    // It worked: whatever the bar was saying no longer applies.
    if (Array.isArray(r.failed) && r.failed.length) showNotice(MESSAGES.storage, { persistent: true })
    else showNotice(null)
    if (Array.isArray(r.held) && r.held.length) {
      const waits = heldWaits.current
      let cancel = null
      cancel = awaitHeldPauses(r.held, {
        storage: readStorage(),
        pending: pendingAcks.current,
        onSilence: ({ message }) => {
          if (cancel) waits.delete(cancel)
          showNotice(message)
        }
      })
      waits.add(cancel)
    }
  }, [showNotice])

  const openContextMenu = useCallback((e) => {
    if (e) e.preventDefault()
    if (bar && typeof bar.showContextMenu === 'function') {
      try { bar.showContextMenu() } catch {}
    }
  }, [bar])

  const focusMain = useCallback(() => {
    if (bar && typeof bar.focusMain === 'function') {
      try { bar.focusMain() } catch {}
      return
    }
    window.location.href = '/login'
  }, [bar])

  // A timer FORM window — the same window, popupKey scheme and size Layout's
  // spawnTimerWindow uses, plus ?pk= so the new window records its key and the
  // bar can focus it later.  For a person who cannot record time (no
  // can_post_time): what Start always did before one-click timers.
  const openNewForm = useCallback(() => {
    if (!(window.electronAPI && window.electronAPI.openPopupWindow)) return
    const pk = newPopupKey()
    safeOpenPopupWindow(pk, tagRouteWithPopupKey('/popup/timer/new', pk), timerWindowOptions())
  }, [])

  // ⏱ Start a timer / "+" — one click, no window: the new timer ticks with no
  // matter (picked later from its form or the main window's ⏱ Timers), and
  // the store pauses whichever of my timers was running at the same instant.
  const startTimer = useCallback(async () => {
    if (!canPost) { openNewForm(); return }
    const t = Date.now()
    if (t - lastStartAt.current < START_GUARD_MS && t >= lastStartAt.current) return
    lastStartAt.current = t
    let r
    try {
      r = await timerStore.startNewTimer({})
    } catch {
      r = { ok: false, reason: 'storage', message: MESSAGES.storage }
    }
    report(r)
    if (r && r.ok) setSelection(null)
  }, [canPost, openNewForm, report])

  // The legacy slot is the MAIN window's in-page timer while old code holds
  // it; the bar cannot pause it or open it elsewhere, so it brings the main
  // window up and asks Layout to show that timer.  (Once the new main window
  // has adopted it, it is an ordinary v2 timer and none of this applies.)
  const showLegacyTimer = useCallback(() => {
    focusMain()
    sendTimerCommand(LEGACY_SLOT_KEY, 'focus', { storage: readStorage() })
  }, [focusMain])

  // On silence from a window that recorded no popupKey: reopen the timer only
  // if no window holds it any more (its record released when it closed, or
  // dead).  Reopening one that is still held, under a key we can only guess,
  // would open a SECOND window on it — see the header.
  const reopenUnlessHeld = useCallback((key, ownerNow) => {
    const st = ownerState(ownerNow, Date.now())
    if (st === 'live' || st === 'quiet') {
      sendTimerCommand(key, 'focus', { storage: readStorage() })
      return
    }
    reopenTimerWindow(key, popupKeyForSlot(key))
  }, [])

  // ↗ — bring this timer's form window forward, or open one on its slot
  // (lib/timerWindows.js openTimerForm: by its recorded popupKey, else a
  // 'focus' command to a window that recorded none, else a new window).
  // Also what a ⏸ that paused a v2 timer does next (toggleRow).
  const openRow = useCallback(async (row) => {
    if (!row || !readStorage()) return
    if (isLegacySlotKey(row.key) && !row.v2) { showLegacyTimer(); return }
    let how = null
    try {
      how = await openTimerForm(row.key, { storage: readStorage(), pending: pendingAcks.current })
    } catch {
      how = null
    }
    if (how && how.how === 'missing') {
      // Gone since the bar drew it: say why when the store knows
      // (matter.blocked removed a resume timer), else "saved or discarded".
      let note = null
      try { note = timerStore.removalNote(row.key, { tid: row.slot ? row.slot.tid : undefined }) } catch { note = null }
      showNotice((note && note.message) || MESSAGES.missing)
    }
  }, [showLegacyTimer, showNotice])

  // ⏯ on a timer from before the update — today's path: through the owning
  // window, never by editing the slot.  A 'pause' sent to it then brings that
  // window (its form) forward, as a v2 pause opens its form below
  // (openFormAfterHeldPause: at once by its popupKey; with none, only after
  // the window acknowledged the pause — it answers only its last command).
  const toggleOldTimer = useCallback((row) => {
    const s = readStorage()
    if (!row || !s) return
    if (isLegacySlotKey(row.key)) { showLegacyTimer(); return }
    const owner = readOwner(s, row.key)
    const st = ownerState(owner, Date.now())
    if (st === 'none' || st === 'dead') {
      // No window to apply it — open the timer instead (contract D), under
      // the recorded key when there is one.
      reopenTimerWindow(row.key, (owner && owner.pk) || popupKeyForSlot(row.key))
      return
    }
    // 'live' or 'quiet': a quiet record is usually a throttled window, and a
    // storage event still reaches it at once — so ask, and wait for the reply.
    const action = row.slot && !row.slot.paused ? 'pause' : 'resume'
    const cmd = sendTimerCommand(row.key, action, { storage: s })
    if (!cmd) return
    awaitAck({
      storage: s,
      storageKey: row.key,
      cmd,
      pending: pendingAcks.current,
      onSilence: (ownerNow) => {
        const pk = (ownerNow && ownerNow.pk) || (owner && owner.pk)
        // Recorded key: reopening under it focuses the window if it is there.
        if (pk) { reopenTimerWindow(row.key, pk); return }
        // No key: still held (live OR quiet — a frozen window goes quiet) →
        // ask it to come forward, never open a second window on it; released
        // or dead → reopen it on its slot.
        reopenUnlessHeld(row.key, ownerNow)
      }
    })
    if (action === 'pause') {
      openFormAfterHeldPause(row.key, cmd, { storage: s, pending: pendingAcks.current }).catch(() => {})
    }
  }, [showLegacyTimer, reopenUnlessHeld])

  // ⏯ — a v2 timer is paused or resumed right here, in its slot (the
  // one-running rule pauses my other timers at the same instant); a timer
  // from before the update goes through its window.
  // `row` is the row the bar SHOWED when clicked: ⏸ pauses and ▶ resumes, and
  // both are no-ops on a timer already in that state (another window got
  // there first), so a duplicate click never does the opposite.
  const toggleRow = useCallback(async (row, e) => {
    if (!row) return
    // The second click of a double click (see TOGGLE_GUARD_MS).
    if (e && Number(e.detail) > 1) return
    // The second click of a double click on "⏱ Start a timer" lands on ⏯,
    // which takes the Start button's place the moment the timer starts:
    // never pause a timer this bar started a moment ago.
    const t = Date.now()
    if (t - lastStartAt.current < START_GUARD_MS && t >= lastStartAt.current) return
    if (!row.v2) { toggleOldTimer(row); return }
    const last = lastToggleAt.current.get(row.key)
    if (last != null && t - last < TOGGLE_GUARD_MS && t >= last) return
    lastToggleAt.current.set(row.key, t)
    let r
    let paused = null
    try {
      if (row.ticking) {
        r = timerStore.pause(row.key, {})
        // Rewritten by old code since the bar last read it (a race): adopt
        // it first, as timerStore.toggle does — the bar cannot ask, so one
        // that needs a confirm comes back 'needs-confirm' and is explained.
        if (r && !r.ok && r.reason === 'needs-adopt') {
          const a = await timerStore.adoptV1(row.key, {})
          r = a && a.ok ? timerStore.pause(row.key, {}) : a
        }
        // Really paused: written here, or — a timer a pre-update window
        // holds (its heartbeat has no v:2) — sent to that window as a command.
        paused = r && r.ok && (r.changed === true || r.viaCommand) ? r : null
      } else {
        r = await timerStore.startOrResume(row.key, {})
      }
    } catch {
      r = { ok: false, reason: 'storage', message: MESSAGES.storage }
    }
    report(r)
    // ⏸ that really paused it (not a no-op on a timer already paused, not a
    // refusal): its form, exactly as ↗ opens it (James, 2026-10-15) — in
    // this window only; a pre-update window's timer as toggleOldTimer does.
    // ▶ opens nothing; the guards above make one click one pause, so one form.
    if (paused && paused.viaCommand) {
      openFormAfterHeldPause(row.key, paused.viaCommand, { storage: readStorage(), pending: pendingAcks.current }).catch(() => {})
    } else if (paused) {
      await openRow(row)
    }
  }, [toggleOldTimer, report, openRow])

  // Plain function, not useCallback: the list is re-read at the click anyway.
  const chooseTimer = async () => {
    if (!bar || typeof bar.showTimerMenu !== 'function') return
    let list = []
    try { list = timerStore.listMyTimers(uid) } catch { list = [] }
    const items = list.slice(0, MENU_MAX_ITEMS).map(r => ({
      id: r.key,
      label: clip(`${r.ticking ? '▶' : '⏸'} ${formatBarElapsed(r.elapsed)} · ${r.label}`, MENU_MAX_LABEL)
    }))
    let picked = null
    try { picked = await bar.showTimerMenu(items) } catch { picked = null }
    if (picked && list.some(r => r.key === picked)) setSelection({ key: picked, at: Date.now() })
  }

  const shell = (children, label) => (
    <div
      data-testid="timer-bar"
      role="toolbar"
      aria-label={label}
      onContextMenu={openContextMenu}
      {...dragProps}
      style={{
        ...DRAG,
        boxSizing: 'border-box',
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        // Scales with the window like everything else (see BTN): five
        // controls share 380px, and at 150% zoom fixed 8px gaps would eat the
        // matter name.
        gap: 'min(8px, 16vh)',
        padding: '0 9px',
        background: COLORS.bg,
        color: COLORS.text,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        overflow: 'hidden',
        userSelect: 'none',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: TEXT_FONT
      }}>
      {children}
    </div>
  )

  const noticeView = notice && (
    <span data-testid="timer-bar-notice" role="status" aria-live="polite" title={notice.text}
      style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: COLORS.paused, fontSize: SMALL_FONT }}>
      {notice.text}
    </span>
  )

  // Opened in a browser (no desktop bar API): explain rather than half-work.
  if (!desktop) {
    return shell(
      <span style={{ color: COLORS.muted }}>
        ⏱ The timer bar is part of the Omnia desktop app.{' '}
        <a href="/time-entries" style={{ ...NO_DRAG, color: COLORS.text }}>Time entries</a>
      </span>,
      'Timer bar'
    )
  }

  // Signed out: nothing about anyone's timers — just the way back in.
  if (!token) {
    return shell(
      <button type="button" data-testid="timer-bar-sign-in" onClick={focusMain}
        style={{ ...NO_DRAG, flex: 1, height: BTN, border: 'none', borderRadius: 8, background: COLORS.button, color: COLORS.text, fontSize: TEXT_FONT, cursor: 'pointer' }}>
        🔒 Sign in to Omnia
      </button>,
      'Timer bar — signed out'
    )
  }

  // The signed-in person cannot record time (the in-page ⏱ button is hidden
  // for them too).  Only decided when the loaded user IS the token's user — a
  // stale user from before a sign-in change never blocks the bar.
  if (userIsSignedIn && !hasPermission(user, 'time_entries')) {
    return shell(
      <span style={{ color: COLORS.muted, fontSize: SMALL_FONT }}>⏱ Time entry isn't turned on for your account.</span>,
      'Timer bar'
    )
  }

  if (!current) {
    return shell(
      <>
        {noticeView}
        <button type="button" data-testid="timer-bar-start" onClick={startTimer}
          aria-label={canPost ? 'Start a timer now — pick the matter later' : 'Open a new timer'}
          title={canPost ? 'Start a timer now — pick the matter later' : 'Open a new timer'}
          style={noticeView
            ? { ...iconButton, background: COLORS.accent, color: '#ffffff' }
            : { ...NO_DRAG, flex: 1, height: BTN, border: 'none', borderRadius: 8, background: COLORS.accent, color: '#ffffff', fontSize: TEXT_FONT, fontWeight: 600, cursor: 'pointer' }}>
          {noticeView ? '⏱' : '⏱ Start a timer'}
        </button>
      </>,
      'Timer bar'
    )
  }

  const ticking = current.ticking
  const label = current.label
  const others = timers.length - 1

  return shell(
    <>
      <button type="button" data-testid="timer-bar-toggle" onClick={(e) => toggleRow(current, e)}
        aria-label={ticking ? 'Pause timer' : 'Resume timer'}
        title={ticking ? 'Pause this timer' : 'Resume this timer'}
        style={{ ...iconButton, color: ticking ? COLORS.paused : COLORS.ticking }}>
        {ticking ? '⏸' : '▶'}
      </button>
      <span data-testid="timer-bar-elapsed"
        style={{ flex: 'none', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: TIME_FONT, fontWeight: 700, color: ticking ? COLORS.ticking : COLORS.paused }}>
        {formatBarElapsed(current.elapsed)}
      </span>
      {noticeView || (
        <span data-testid="timer-bar-label" title={label}
          style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: COLORS.text }}>
          {label}
        </span>
      )}
      {others > 0 && (
        <button type="button" data-testid="timer-bar-more" onClick={chooseTimer}
          aria-label={`${others} more timer${others === 1 ? '' : 's'} — choose which one to show`}
          title="Choose which timer the bar shows"
          style={{ ...iconButton, width: 'auto', padding: '0 7px', fontSize: SMALL_FONT, fontWeight: 600, background: COLORS.accent }}>
          +{others}
        </button>
      )}
      <button type="button" data-testid="timer-bar-open" onClick={() => openRow(current)}
        aria-label="Open this timer"
        title="Open the full timer form"
        style={iconButton}>
        ↗
      </button>
      {/* Always a way to start another timer — the in-page ⏱ button is
          hidden while this bar is on. */}
      <button type="button" data-testid="timer-bar-new" onClick={startTimer}
        aria-label="Start a new timer"
        title="Start a new timer"
        style={iconButton}>
        +
      </button>
    </>,
    `Timer bar — ${label}`
  )
}
